import { TERMINAL_PAYMENT_HISTORY_ENABLED } from '../../Common/config/terminalFeatures';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { checkLogin, setOtpVerified } from '../../Common/store';
import { terminalLogout } from '../api/cp';
import { LogOut, Settings } from 'lucide-react';
import AppLogoutConfirmModal from '../components/AppLogoutConfirmModal';
import DemoModeBanner from '../components/DemoModeBanner';
import AppDashboard from './AppDashboard';
import AppDashboard_1 from './AppDashboard_1';
import MappedPharmacyDashboard from './MappedPharmacyDashboard';
import { MANUAL_PAYMENT_ENV_DEFAULTS } from '../../Common/hooks/useManualPaymentConfig';
import { getAppLogoAssetUrl, getAppLogoFallbackUrl, getAppHeaderLogoStyle, getTerminalHeaderStyle } from '../../Common/constants/adminImages';

/** dev URL이면 AppDashboard_1, 아니면 AppDashboard (수기결제와 동일한 방식) */
// const isDevUrl = false;
const isDevUrl = typeof window !== 'undefined' && window.location.href.includes('dev');

/**
 * 앱 대시보드 - URL에 dev 포함 시 AppDashboard_1, 아니면 AppDashboard
 */
function AppDashboardPage() {
    const isMappedMode = MANUAL_PAYMENT_ENV_DEFAULTS.pharmacySelectionMode === 'MAPPED';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [showPasswordNotice, setShowPasswordNotice] = useState(() => location.state?.showPasswordChangeNotice === true);

    useEffect(() => {
        if (!showPasswordNotice) return undefined;
        navigate(location.pathname, { replace: true, state: {} });
        const timer = setTimeout(() => setShowPasswordNotice(false), 2000);
        return () => clearTimeout(timer);
    }, [showPasswordNotice, navigate, location.pathname]);


    const confirmLogout = async () => {
        try {
            setLogoutLoading(true);
            try {
                await terminalLogout();
            } catch (e) {}
            try { sessionStorage.removeItem('terminal_demo_mode'); } catch (_) {}
            dispatch(setOtpVerified(false));
            dispatch(checkLogin(false));
            setLogoutModalOpen(false);
            navigate('/app');
        } finally {
            setLogoutLoading(false);
        }
    };

    return (
        <div style={{
            height: '100dvh',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '768px',
            margin: '0 auto',
            background: '#FFFFFF',
            overflow: 'hidden',
        }}>
            {showPasswordNotice && (
                <div role="status" style={{
                    position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 3000, maxWidth: 'calc(100% - 32px)', padding: '10px 15px',
                    borderRadius: 10, background: '#191f28', color: '#fff', fontSize: 13,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.2)', textAlign: 'center',
                }}>
                    초기 비밀번호를 사용 중입니다. 설정에서 비밀번호를 변경해 주세요.
                </div>
            )}
            <style>
                {`
                    button, a, [onClick] {
                        outline: none !important;
                        -webkit-tap-highlight-color: transparent;
                        -webkit-touch-callout: none;
                        user-select: none;
                        transition: transform 0.1s ease, opacity 0.1s ease;
                    }
                    button:focus, a:focus, [onClick]:focus { outline: none !important; }
                    button:active, a:active, [onClick]:active {
                        outline: none !important;
                        transform: scale(0.96);
                        opacity: 0.8;
                    }
                `}
            </style>

            {/* 헤더: 로그아웃(왼쪽) | 로고(가운데) */}
            <div style={{
                background: 'linear-gradient(160deg, var(--brand-gradient-start) 0%, var(--brand-gradient-mid) 60%, var(--brand-gradient-end) 100%)',
                ...getTerminalHeaderStyle(),
                height: '62px',
                padding: '0 12px 0 10px',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
                flexShrink: 0,
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 2px',
                    width: '100%',
                    position: 'relative',
                }}>
                    <button
                        type="button"
                        onClick={() => setLogoutModalOpen(true)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                        }}
                    >
                        <LogOut size={22} color="var(--terminal-header-foreground, #FFFFFF)" />
                    </button>

                    <img
                        src={getAppLogoAssetUrl()}
                        alt=""
                        onError={(e) => { e.target.src = getAppLogoFallbackUrl(); }}
                        style={getAppHeaderLogoStyle({
                            width: '150px',
                            height: 'auto',
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            pointerEvents: 'none',
                        })}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {TERMINAL_PAYMENT_HISTORY_ENABLED && (isMappedMode || isDevUrl) && (
                            <button
                                type="button"
                                onClick={() => navigate('/app/payment-list')}
                                aria-label="결제내역"
                                style={{
                                    padding: '6px 10px',
                                    background: '#FFFFFF',
                                    border: '1px solid var(--terminal-header-button-border, rgba(255,255,255,0.45))',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    lineHeight: 1.2,
                                    fontWeight: 700,
                                    color: 'var(--brand-gradient-mid)',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                내역
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => navigate('/app/settings')}
                            aria-label="설정"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#FFFFFF',
                            }}
                        >
                            <Settings size={22} color="var(--terminal-header-foreground, #FFFFFF)" />
                        </button>
                    </div>
                </div>
            </div>

            <DemoModeBanner />

            {/* 본문: dev URL → AppDashboard_1, 아니면 AppDashboard */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {isMappedMode ? <MappedPharmacyDashboard /> : isDevUrl ? <AppDashboard_1 hideHeader /> : <AppDashboard hideHeader />}
            </div>

            <AppLogoutConfirmModal
                open={logoutModalOpen}
                onCancel={() => {
                    if (!logoutLoading) setLogoutModalOpen(false);
                }}
                onConfirm={confirmLogout}
                loading={logoutLoading}
            />
        </div>
    );
}

export default AppDashboardPage;
